import {
  Avatar,
  Box,
  Container,
  Divider,
  Heading,
  HStack,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { FiMessageSquare } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { useMyConversations } from '../../hooks/useMessages';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { EmptyState } from '../common/EmptyState';
import { formatRelativeTime } from '../../utils/formatters';

export default function MessagesList() {
  const { currentUser } = useAuth();
  const { conversations, loading } = useMyConversations(currentUser?.uid);
  const navigate = useNavigate();

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  if (loading) {
    return <LoadingSpinner message="Loading messages..." />;
  }

  return (
    <Container maxW="xl" py={6} px={0}>
      <VStack spacing={0} align="stretch">
        <Box px={4} pb={4}>
          <Heading size="lg">Messages</Heading>
        </Box>

        {conversations.length === 0 ? (
          <Box px={4}>
            <EmptyState
              title="No messages yet"
              description="Start a conversation by visiting a researcher or user profile."
              icon={FiMessageSquare}
            />
          </Box>
        ) : (
          <Box bg={cardBg} rounded="xl" borderWidth={1} borderColor={borderColor} overflow="hidden">
            <VStack spacing={0} align="stretch" divider={<Divider borderColor={borderColor} />}>
              {conversations.map((conv) => {
              const otherUid = conv.participants.find((p) => p !== currentUser?.uid);
              const otherData = conv.participantData?.[otherUid] || {};
              const otherName = otherData.isFullyAnonymous ? 'Anonymous' : (otherData.username || 'User');
              const isUnread = conv.unreadBy?.includes(currentUser?.uid);
              const lastText = conv.lastMessage?.text || '';
              const isMine = conv.lastMessage?.senderUid === currentUser?.uid;

              return (
                <HStack
                  key={conv.id}
                  spacing={3}
                  px={4}
                  py={3}
                  cursor="pointer"
                  onClick={() => navigate(`/messages/${conv.id}`)}
                  _hover={{ bg: hoverBg }}
                  transition="background 0.15s"
                >
                  <Avatar
                    size="md"
                    src={otherData.isFullyAnonymous ? undefined : otherData.avatarUrl}
                    name={otherName}
                    bg={otherData.isFullyAnonymous ? 'gray.400' : 'orange.400'}
                  />

                  <Box flex="1" minW={0}>
                    <HStack justify="space-between" align="center">
                      <Text
                        fontWeight={isUnread ? 'bold' : 'normal'}
                        fontSize="sm"
                        noOfLines={1}
                      >
                        {otherName}
                      </Text>
                      <Text fontSize="xs" color="gray.400" flexShrink={0}>
                        {formatRelativeTime(conv.updatedAt)}
                      </Text>
                    </HStack>
                    <HStack spacing={1} align="center">
                      <Text
                        fontSize="sm"
                        color={isUnread ? 'gray.800' : 'gray.500'}
                        fontWeight={isUnread ? 'medium' : 'normal'}
                        noOfLines={1}
                        flex="1"
                      >
                        {isMine ? 'You: ' : ''}{lastText}
                      </Text>
                      {isUnread && (
                        <Box w="8px" h="8px" borderRadius="full" bg="orange.500" flexShrink={0} />
                      )}
                    </HStack>
                  </Box>
                </HStack>
              );
            })}
            </VStack>
          </Box>
        )}
      </VStack>
    </Container>
  );
}
